import { createCanvas, destroyCanvas } from "@/libs/canvas"
import { distance, rad2deg } from "@/libs/geometry/geometry"
import { WebGLContextLostError } from "@/libs/gl/error"
import { getImageSourceDimensions, isVideoFrame, type ImageSource } from "@/libs/image"
import { noop } from "@/libs/lodash"
import { createAggregatedProgress } from "@/libs/progress"
import { interceptFetchRequest } from "@/libs/request/fetch-interceptor"
import { createResponseWithProgress, type Progress } from "@/libs/request/fetch-progress"
import { throttleMark, type PerformanceEntry } from "@/libs/telemetry/performance"
import { getFetchEntriesMeasurements } from "@/libs/telemetry/performance/fetch-entries"
import type * as Mediapipe from "@mediapipe/tasks-vision"
import { FaceLandmarker } from "@mediapipe/tasks-vision"
import type { Graph } from "../../framework"
import type { FaceBox, FaceDetection } from "../types.d"
import { getDependencies } from "./dependencies"
import { getInferenceDelegate } from "./inference-delegate"

export type { FaceDetection }

type RunningMode = Mediapipe.FaceLandmarkerOptions["runningMode"]

export type Options = {
  performance?: PerformanceEntry
  onProgress?: (progress: number) => void
  runningMode?: RunningMode
}

export const create = async <I extends unknown[], O extends unknown[]>(
  graph: Graph<I, O>,
  opts?: Options,
) => {
  const perf = opts?.performance

  const assets = (await getDependencies()).map((dep) => dep.url) as [string, string, string]

  const loadingPerf = perf?.mark("cv:mediapipe-loading")

  const detector = await createFromOptions(...assets, {
    onProgress: opts?.onProgress,
    runningMode: opts?.runningMode ?? "VIDEO",
  })

  const fetchMeasurements = await getFetchEntriesMeasurements((entry) =>
    assets.some((asset) => entry.name.includes(asset)),
  )
  loadingPerf?.measure({ children: fetchMeasurements })

  const initPerf = perf?.mark("cv:mediapipe-initialization")

  const detect: (imageSource: TexImageSource) => Mediapipe.FaceLandmarkerResult =
    opts?.runningMode === "IMAGE"
      ? (imageSource) => detector.landmarker.detect(imageSource)
      : (imageSource) => detector.landmarker.detectForVideo(imageSource, Date.now())

  // pre-initializing GPU models with an empty frame to speed up the first execution
  detect(new ImageData(new Uint8ClampedArray(4), 1, 1))
  initPerf?.measure()

  const inferenceMark = throttleMark(opts?.performance, 1000 / 2)

  let isContextLost = false
  const abortController = new AbortController()
  detector.canvas.addEventListener(
    "webglcontextlost",
    (e) => {
      e.preventDefault()
      isContextLost = true
    },
    { once: true, signal: abortController.signal },
  )

  const node = graph.node<[ImageSource], [FaceDetection | null]>({
    process: async (ctx) => {
      if (isContextLost)
        throw new WebGLContextLostError("Failed to process frame: gl.CONTEXT_LOST_WEBGL")

      const inferencePerf = inferenceMark("cv:mediapipe-inference")
      const imageSource = ctx.in(0)

      // WORKAROUND: MediaPipe relies on `width` and `height` and doesn't aware of VideoFrame API
      // https://github.com/google/mediapipe/issues/2726#issuecomment-1045417170
      if (isVideoFrame(imageSource)) {
        Object.assign(imageSource, {
          // NOTE: WebGL texture uses frame.visibleRect as its source of pixels
          width: imageSource.visibleRect!.width,
          height: imageSource.visibleRect!.height,
        })
      }

      try {
        const { faceLandmarks, facialTransformationMatrixes: [transformation] = [] } =
          detect(imageSource)

        let [landmarks] = faceLandmarks
        let detection: FaceDetection | null = null

        if (landmarks) {
          landmarks = toFaceLandmarks(landmarks)
          const faceBox = getFaceBox(landmarks, getImageSourceDimensions(imageSource))
          const { pitch, yaw, roll, distance } = toTransformation(transformation)

          detection = {
            ...faceBox,
            boundingBox: getBoundingBox(landmarks),
            landmarks,
            rotation: { pitch, yaw, roll },
            distance,
          }
        }

        inferencePerf?.measure()
        ctx.out(0, detection)
      } catch (e) {
        throw isContextLost
          ? new WebGLContextLostError("Failed to process frame: gl.CONTEXT_LOST_WEBGL")
          : e
      }
    },
    destroy: () => {
      abortController.abort()
      detector.destroy()
    },
  })

  return node
}

const createFromOptions = async (
  wasmBinaryPath: string,
  wasmLoaderPath: string,
  modelAssetPath: string,
  {
    runningMode,
    ...opts
  }: {
    runningMode?: RunningMode
    onProgress?: (progress: number) => void
  } = {},
) => {
  // MediaPipe uses `fetch` API to load the task and wasm files
  // hook into it, to track the loading progress

  // `wasmLoaderPath` is not included in the progress tracking,
  // since it‘s a JavaScript file that is loaded by the browser and not by the `fetch` API
  const assets = [wasmBinaryPath, modelAssetPath]
  const onProgress = opts.onProgress
    ? createAggregatedProgress({
        coeffs: Array.from({ length: assets.length }, () => 1 / assets.length),
        onProgress: opts.onProgress,
        transform: (p: Progress) => p.complete,
      })
    : undefined

  const interceptor = interceptFetchRequest((request, fetch) => {
    const idx = assets.findIndex((asset) => request.url.endsWith(asset))
    if (idx === -1) return

    return fetch(request).then((response) =>
      createResponseWithProgress(response, onProgress?.bind(null, idx) ?? noop),
    )
  })

  const canvas = createCanvas(1, 1)

  const abortController = new AbortController()
  let isContextLost = false

  canvas.addEventListener(
    "webglcontextlost",
    (e) => {
      e.preventDefault()
      isContextLost = true
    },
    { once: true, signal: abortController.signal },
  )

  try {
    const landmarker = await FaceLandmarker.createFromOptions(
      { wasmLoaderPath, wasmBinaryPath },
      {
        canvas,
        numFaces: 1,
        // outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        runningMode,
        baseOptions: {
          delegate: getInferenceDelegate(),
          modelAssetPath,
          // NOTE: the solution with blandshapes is too slow on mobile, 70ms+ on Pixel 4a, 35ms+ on iPhone 8+
          // modelAssetPath: `https://storage.googleapis.com/mediapipe-assets/face_landmarker_with_blendshapes.task?generation=1680506162229625`,
        },
      },
    )

    return {
      landmarker,
      canvas,
      destroy: () => {
        landmarker.close()
        abortController.abort()
        destroyCanvas(canvas)
      },
    }
  } catch (cause) {
    destroyCanvas(canvas)

    throw isContextLost
      ? new WebGLContextLostError("Failed to create FaceLandmarker: gl.CONTEXT_LOST_WEBGL")
      : new Error("Failed to create FaceLandmarker", { cause })
  } finally {
    abortController.abort()
    interceptor.terminate()
  }
}

/** @returns width and height of the face as well as x, y coordinates of the face center  */
const getFaceBox = (
  landmarks: { x: number; y: number }[],
  frame: { width: number; height: number } = { width: 1, height: 1 },
): FaceBox => {
  const forehead = toPx(landmarks[10], frame)
  const chin = toPx(landmarks[152], frame)
  const nose = toPx(landmarks[195], frame)
  const leftCheek = toPx(landmarks[234], frame)
  const rightCheek = toPx(landmarks[454], frame)

  const x = nose.x
  const y = nose.y
  const width = distance(leftCheek, rightCheek)
  const height = distance(forehead, chin)

  return { x, y, width, height }
}

/** Adjusts FaceMesh landmarks to sit more accurately */
export const toFaceLandmarks = (landmarks: Mediapipe.NormalizedLandmark[]) => {
  // Fix insufficient forehead coverage by FaceMesh landmarks
  // The fix is aligned with the backend‘s one: https://gitlab.com/haut.ai-team/sasuke/algorithms/-/blob/master/hautai/saas/algorithms/base/selfie/facemesh/algorithm.py#L94
  const FIX_FOREHEAD_COEFF = 1.0

  const delta = {
    x: landmarks[151].x - landmarks[10].x,
    y: landmarks[151].y - landmarks[10].y,
  }

  for (const i of [67, 109, 10, 338, 297]) {
    landmarks[i].x -= delta.x * FIX_FOREHEAD_COEFF * 1.0
    landmarks[i].y -= delta.y * FIX_FOREHEAD_COEFF * 1.0
  }

  for (const i of [103, 332, 104, 69, 108, 151, 337, 299, 333]) {
    landmarks[i].x -= delta.x * FIX_FOREHEAD_COEFF * 0.5
    landmarks[i].y -= delta.y * FIX_FOREHEAD_COEFF * 0.5
  }

  for (const i of [70, 63, 105, 66, 107, 336, 296, 334, 293, 300]) {
    landmarks[i].x -= delta.x * FIX_FOREHEAD_COEFF * 0.2
    landmarks[i].y -= delta.y * FIX_FOREHEAD_COEFF * 0.2
  }

  return landmarks
}

/**
 * @param matrix.data - representation of transformation matrix
 *
 * | rx11 ry12 rz13 tx |
 * | rx21 ry22 rz23 ty |
 * | rx31 ry32 rz33 tz |
 * |  0   0   0   1 |
 *
 * as liner array
 *
 * [rx11, rx21, rx31, 0, ry12, ry22, ry32, 0, rz13, rz23, rz33, 0, tx, ty, tz, 1]
 */
const toTransformation = (matrix?: { data: number[] }) => {
  if (!matrix)
    return {
      roll: 0,
      yaw: 0,
      pitch: 0,
      distance: 0,
    }

  const data = matrix.data

  const r11 = data[0 * 4 + 0]
  const r12 = data[1 * 4 + 0]
  const r13 = data[2 * 4 + 0]
  const r23 = data[2 * 4 + 1]
  const r33 = data[2 * 4 + 2]

  const roll = Math.atan2(-r12, r11)
  const yaw = Math.asin(r13)
  const pitch = Math.atan2(-r23, r33)

  const tz = data[3 * 4 + 2]

  return {
    roll: rad2deg(roll),
    yaw: rad2deg(yaw),
    pitch: rad2deg(pitch),
    distance: -tz,
  }
}

/** Converts landmark coordinates from normalized to absolute */
const toPx = (landmark: { x: number; y: number }, frame: { width: number; height: number }) => ({
  x: landmark.x * frame.width,
  y: landmark.y * frame.height,
})

/** Derives bounding box for the given set of points */
const getBoundingBox = (points: { x: number; y: number }[]) => {
  let minX = Number.MAX_SAFE_INTEGER
  let minY = Number.MAX_SAFE_INTEGER
  let maxX = Number.MIN_SAFE_INTEGER
  let maxY = Number.MIN_SAFE_INTEGER

  // for loop is faster then .forEach()
  for (let i = 0, len = points.length; i < len; ++i) {
    const { x, y } = points[i]
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
}
